import React, { useMemo, useState, useEffect } from "react";
import { createCedingSchemeExtractionJob } from "../Utils/loaApi";
import { useNavigate } from "react-router-dom";
import "../../../../css/LOA/CedingSchemePage.css";
import { saveCedingSchemeCaseExtraction } from "../Utils/saveCedingSchemeExtraction";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { useAdminAdviserMails } from "../../../../context/AdminAdviserEmailsContext";
import { doc, getDoc } from "firebase/firestore";
import { dbLOA } from "../../../../backend/firebaseconfig";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/remove.svg";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import Banner from "../../AML/Components/Banners/Banner";
import {ReactComponent as BackIcon} from "../../../../assets/icons/Back.svg";
import * as XLSX from "xlsx";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";

const statusLabels = {
  found: "Found",
  partial: "Partial",
  missing: "Missing",
};

const statusDescriptions = {
  found: "Information found in document",
  partial: "Needs review or confirmation",
  missing: "Not provided in document",
};

const createEmptyScheme = (index = 0) => ({
  id: crypto.randomUUID(),
  name: `Ceding Scheme ${index + 1}`,
  files: [],
  filePasswords: {},
  savedFiles: [],
  extractionJson: null,
  savedExtractionId: null,
  activeFileIndex: 0,
  pdfPage: 1,
  notes: "",
  extracting: false,
  saving: false,
  error: "",
  savedMessage: "",
});

const CedingSchemePage = ({ extractionId, ownerEmail }) => {
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { currentAdviserEmail } = useAdminAdviserMails();
  const isAdviserAdmin = userRole === "Administrators";
  const [caseMeta, setCaseMeta] = useState({
    status: "",
    progressMessage: "",
    error: "",
    additionalInstructions: "",
    createdBy: "",
    createdAt: null,
  });

  const [schemes, setSchemes] = useState([createEmptyScheme(0)]);
  const [activeSchemeIndex, setActiveSchemeIndex] = useState(0);
  const [caseExtractionId, setCaseExtractionId] = useState(
    extractionId || null,
  );
  const navigate = useNavigate();
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const LOA_DASHBOARD_ROUTE = "/pro-ceding/dashboard"; // change if your dashboard route is different
  const [extractingAll, setExtractingAll] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [pageError, setPageError] = useState("");
  const [showJson] = useState(false);

  const activeScheme = schemes[activeSchemeIndex] || schemes[0];

  const isBusy = extractingAll || loadingSaved;

  const summaryQuestions = [
    "Client's Name",
    "Pension Provider Name",
    "Telephone number",
    "Email address",
    "Policy Number or Plan Number",
    "Type of Pension Contract",
    "Start Date",
    "Valuation Date",
    "Is the pension active or paid up?",
    "Selected Retirement Date",
    "Funds currently invested in with percentage split",
    "Alternative funds available within this contract",
    "With-Profits asset allocation",
    "Last 5 years With-Profits bonus declarations",
    "Current fund value",
    "Current transfer value",
    "Exit penalties or MVR",
    "Exit penalty removal date",
    "Paid-up projection at age XX",
    "Protected tax free cash at A-day",
    "Life cover included in this plan",
    "Section 32 / GMP / Section 9(2b) rights",
    "Guaranteed annuity rates",
    "Guaranteed income in retirement",
    "Ongoing adviser charges",
    "Death benefits",
    "Flexi Access Drawdown availability",
    "Transfer forms to transfer this pension",
  ];

  const downloadSummaryExcel = () => {
    const headers = [
      "Detail",
      ...extractedSchemes.map((scheme, index) =>
        getProviderName(scheme, `Scheme ${index + 1}`),
      ),
    ];

    const rows = summaryQuestions.map((question) => [
      question,
      ...extractedSchemes.map((scheme) => getAnswerValue(scheme, question)),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    worksheet["!cols"] = headers.map((header, index) => ({
      wch: index === 0 ? 36 : Math.max(24, String(header).length + 4),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ceding Scheme Summary");

    XLSX.writeFile(workbook, "ceding-scheme-summary.xlsx");
  };

 const normalizeQuestion = (value = "") =>
   String(value)
     .trim()
     .replace(/^\d+\s*[\).:-]?\s*/, "") // removes "1. ", "1) ", "1 - "
     .replace(/[’‘]/g, "'")
     .replace(/\s+/g, " ")
     .toLowerCase();

 const getAnswerValue = (scheme, question) => {
   const targetQuestion = normalizeQuestion(question);

   const row = scheme?.extractionJson?.answers?.find(
     (item) => normalizeQuestion(item.question) === targetQuestion,
   );

   return row?.answer || "-";
 };
  const getProviderName = (scheme, fallback = "Ceding Scheme") => {
    return (
      scheme?.extractionJson?.summary?.providerName ||
      getAnswerValue(scheme, "Pension Provider Name") ||
      scheme?.name ||
      fallback
    );
  };

  const extractedSchemes = schemes.filter((scheme) => scheme.extractionJson);
  const updateScheme = (schemeId, updater) => {
    setSchemes((prev) =>
      prev.map((scheme) =>
        scheme.id === schemeId
          ? typeof updater === "function"
            ? updater(scheme)
            : { ...scheme, ...updater }
          : scheme,
      ),
    );
  };

  const getFilePasswordKey = (file) => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  const updateFilePassword = (schemeId, file, password) => {
    const passwordKey = getFilePasswordKey(file);

    updateScheme(schemeId, (scheme) => ({
      ...scheme,
      filePasswords: {
        ...(scheme.filePasswords || {}),
        [passwordKey]: password,
      },
    }));
  };

  const hasAnyExtraction = schemes.some((scheme) => !!scheme.extractionJson);

  const addScheme = () => {
    const newScheme = createEmptyScheme(schemes.length);

    setSchemes((prev) => [...prev, newScheme]);
    setActiveSchemeIndex(schemes.length);
    setPageError("");
  };

  const removeScheme = (schemeId) => {
    setSchemes((prev) => {
      if (prev.length === 1) return prev;

      const filtered = prev.filter((scheme) => scheme.id !== schemeId);

      setActiveSchemeIndex((current) => Math.min(current, filtered.length - 1));

      return filtered;
    });
  };

  const validatePdfFiles = (selectedFiles) => {
    const nonPdf = selectedFiles.find(
      (selectedFile) => selectedFile.type !== "application/pdf",
    );

    if (nonPdf) {
      return {
        validFiles: [],
        error: "Please upload PDF files only.",
      };
    }

    return {
      validFiles: selectedFiles,
      error: "",
    };
  };

  const addFilesToScheme = (schemeId, selectedFiles) => {
    const { validFiles, error } = validatePdfFiles(selectedFiles);

    if (error) {
      updateScheme(schemeId, { error });
      return;
    }

    if (!validFiles.length) return;

    updateScheme(schemeId, (scheme) => ({
      ...scheme,
      files: [...scheme.files, ...validFiles],
      error: "",
      savedMessage: "",
    }));
  };

  const removeSelectedFileFromScheme = (schemeId, indexToRemove) => {
    updateScheme(schemeId, (scheme) => {
      const fileToRemove = scheme.files[indexToRemove];
      const passwordKey = fileToRemove
        ? getFilePasswordKey(fileToRemove)
        : null;

      const nextPasswords = { ...(scheme.filePasswords || {}) };

      if (passwordKey) {
        delete nextPasswords[passwordKey];
      }

      return {
        ...scheme,
        files: scheme.files.filter((_, index) => index !== indexToRemove),
        filePasswords: nextPasswords,
        savedMessage: "",
      };
    });
  };

  const handleDrop = (e, schemeId) => {
    e.preventDefault();
    e.stopPropagation();
    addFilesToScheme(schemeId, Array.from(e.dataTransfer.files || []));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileChange = (e, schemeId) => {
    addFilesToScheme(schemeId, Array.from(e.target.files || []));
    e.target.value = "";
  };

  const getSchemeRows = (scheme) => scheme?.extractionJson?.answers || [];

  const getSchemeCounts = (scheme) => {
    return getSchemeRows(scheme).reduce(
      (acc, row) => {
        const status = row.status || "missing";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      { found: 0, partial: 0, missing: 0 },
    );
  };

  const getSchemeCompletion = (scheme) => {
    const rows = getSchemeRows(scheme);
    const counts = getSchemeCounts(scheme);

    if (!rows.length) return 0;

    return Math.round(((counts.found + counts.partial) / rows.length) * 100);
  };

  const overallSummary = useMemo(() => {
    return schemes.reduce(
      (acc, scheme) => {
        const counts = getSchemeCounts(scheme);
        const rows = getSchemeRows(scheme);

        acc.found += counts.found;
        acc.partial += counts.partial;
        acc.missing += counts.missing;
        acc.total += rows.length;
        acc.completedSchemes += scheme.extractionJson ? 1 : 0;

        return acc;
      },
      {
        found: 0,
        partial: 0,
        missing: 0,
        total: 0,
        completedSchemes: 0,
      },
    );
  }, [schemes]);

  const overallCompletionPercent = overallSummary.total
    ? Math.round(
        ((overallSummary.found + overallSummary.partial) /
          overallSummary.total) *
          100,
      )
    : 0;

  useEffect(() => {
    setCaseExtractionId(extractionId || null);
  }, [extractionId]);

  useEffect(() => {
    const loadSaved = async () => {
      if (!extractionId) return;

      try {
        setPageError("");
        setLoadingSaved(true);

        const emailToLoad = ownerEmail || userEmail;

        if (!emailToLoad) {
          setPageError("User email is missing.");
          return;
        }

        const ref = doc(
          dbLOA,
          "Ceding Schemes",
          emailToLoad.trim().toLowerCase(),
          "Extractions",
          extractionId,
        );

        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setPageError("Extraction not found.");
          return;
        }

        const data = snap.data();

        setCaseMeta({
          status: data.status || "",
          progressMessage: data.progressMessage || "",
          error: data.error || "",
          additionalInstructions: data.additionalInstructions || "",
          createdBy: data.createdBy || data.userEmail || data.ownerEmail || "",
          createdAt: data.createdAt || data.createdAtIso || null,
          createdAtIso: data.createdAtIso || "",
        });

        setAdditionalInstructions(data.additionalInstructions || "");

        if (Array.isArray(data.schemes) && data.schemes.length > 0) {
          setSchemes(
            data.schemes.map((scheme, index) => ({
              ...createEmptyScheme(index),
              ...scheme,
              id: scheme.id || crypto.randomUUID(),
              name: scheme.name || `Ceding Scheme ${index + 1}`,
              files: [],
              savedFiles: scheme.savedFiles || scheme.files || [],
              fileNames: scheme.fileNames || [],
              extractionJson: scheme.extractionJson || null,
              savedExtractionId: extractionId,
              notes: scheme.notes || "",
              status: scheme.status || data.status || "",
              progressMessage:
                scheme.progressMessage || data.progressMessage || "",
            })),
          );

          setActiveSchemeIndex(0);
          setCaseExtractionId(extractionId);
          return;
        }

        const savedFiles =
          Array.isArray(data.files) && data.files.length > 0
            ? data.files
            : [
                {
                  fileName: data.fileName || "Saved PDF",
                  fileUrl: data.fileUrl || "",
                  storagePath: data.storagePath || "",
                },
              ];

        setSchemes([
          {
            ...createEmptyScheme(0),
            name: data.schemeName || "Ceding Scheme 1",
            savedFiles,
            fileNames:
              data.fileNames || savedFiles.map((file) => file.fileName),
            extractionJson: data.extractionJson,
            savedExtractionId: extractionId,
            notes: data.notes || data.globalNotes || "",
            status: data.status || "",
            progressMessage: data.progressMessage || "",
          },
        ]);

        setActiveSchemeIndex(0);
        setCaseExtractionId(extractionId);
      } catch (err) {
        setPageError(err.message || "Failed to load extraction.");
      } finally {
        setLoadingSaved(false);
      }
    };

    loadSaved();
  }, [extractionId, ownerEmail, userEmail]);

  useEffect(() => {
    return () => {
      schemes.forEach((scheme) => {
        scheme.savedFiles?.forEach((fileItem) => {
          if (fileItem.fileUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(fileItem.fileUrl);
          }
        });
      });
    };
  }, [schemes]);

  // const handleExtractSchemeOnly = async (scheme) => {
  //   const data = await extractCedingSchemePdf(scheme.files);

  //   const combinedExtraction =
  //     data.extractionJson ||
  //     data.result ||
  //     data.files?.[0]?.extractionJson ||
  //     data.packs?.[0]?.extractionJson;

  //   if (!combinedExtraction) {
  //     throw new Error("No extraction result returned from backend.");
  //   }

  //   return {
  //     ...scheme,
  //     extractionJson: combinedExtraction,
  //     extracting: false,
  //     error: "",
  //     savedMessage: "",
  //   };
  // };

  const handleUploadAndExtractAll = async () => {
    const schemesWithFiles = schemes.filter(
      (scheme) => scheme.files.length > 0,
    );

    if (!schemesWithFiles.length) {
      setPageError("Please upload at least one PDF in one scheme first.");
      return;
    }

    if (!userEmail) {
      setPageError("User email is missing.");
      return;
    }

    try {
      setExtractingAll(true);
      setPageError("");

      setSchemes((prev) =>
        prev.map((scheme) =>
          scheme.files.length > 0
            ? {
                ...scheme,
                extracting: true,
                error: "",
                savedMessage: "Files received. Extraction is processing.",
              }
            : scheme,
        ),
      );

      const effectiveAdviserEmail =
        isAdviserAdmin && currentAdviserEmail ? currentAdviserEmail : userEmail;

      const result = await createCedingSchemeExtractionJob({
        userEmail,
        adviserEmail: effectiveAdviserEmail,
        schemes: schemesWithFiles,
        additionalInstructions,
      });

      setCaseExtractionId(result.extractionId);

      navigate(LOA_DASHBOARD_ROUTE, {
        state: {
          message:
            "Files received. Extraction is processing in the background. The adviser will be emailed once results are ready.",
          extractionId: result.extractionId,
          ownerEmail: result.ownerEmail,
        },
      });
    } catch (err) {
      setPageError(err.message || "Failed to submit extraction job.");

      setSchemes((prev) =>
        prev.map((scheme) => ({
          ...scheme,
          extracting: false,
        })),
      );
    } finally {
      setExtractingAll(false);
    }
  };

  const handleSaveNotes = async (schemeId) => {
    const scheme = schemes.find((item) => item.id === schemeId);
    if (!scheme?.extractionJson) return;

    try {
      updateScheme(schemeId, {
        saving: true,
        error: "",
        savedMessage: "",
      });

      const emailToSave = ownerEmail || userEmail;

      const saved = await saveCedingSchemeCaseExtraction({
        userEmail: emailToSave,
        extractionId:
          caseExtractionId || scheme.savedExtractionId || extractionId,
        schemes,
      });

      setCaseExtractionId(saved.extractionId);

      updateScheme(schemeId, {
        saving: false,
        savedExtractionId: saved.extractionId,
        savedMessage: "Notes saved successfully.",
      });
    } catch (err) {
      updateScheme(schemeId, {
        saving: false,
        error: err.message || "Failed to save notes.",
      });
    }
  };

  const goToSourcePage = (schemeId, source) => {
    const pageNumber = Number(source?.page);

    if (!pageNumber || Number.isNaN(pageNumber)) {
      updateScheme(schemeId, {
        error: "No page reference found for this item.",
      });
      return;
    }

    updateScheme(schemeId, (scheme) => {
      const fileIndex = scheme.savedFiles?.findIndex(
        (file) => file.fileName === source.fileName,
      );

      return {
        ...scheme,
        activeFileIndex: fileIndex >= 0 ? fileIndex : scheme.activeFileIndex,
        pdfPage: pageNumber,
        error: "",
      };
    });
  };

  const goPreviousScheme = () => {
    setActiveSchemeIndex((prev) => Math.max(prev - 1, 0));
  };

  const goNextScheme = () => {
    setActiveSchemeIndex((prev) => Math.min(prev + 1, schemes.length - 1));
  };

  const rows = getSchemeRows(activeScheme);
  const counts = getSchemeCounts(activeScheme);
  const completionPercent = getSchemeCompletion(activeScheme);
  const summary = activeScheme?.extractionJson?.summary || {};
  const missingInformation =
    activeScheme?.extractionJson?.missingInformation || [];

  const activeFile =
    activeScheme?.savedFiles?.[activeScheme.activeFileIndex] ||
    activeScheme?.files?.[activeScheme.activeFileIndex];

  const activeFileUrl =
    activeFile?.fileUrl ||
    (activeFile instanceof File ? URL.createObjectURL(activeFile) : "");

  const pdfViewerUrl =
    activeFileUrl && activeScheme?.pdfPage
      ? `${activeFileUrl}#page=${activeScheme.pdfPage}`
      : activeFileUrl || "";

  const getReadableDate = (value) => {
    if (!value) return "";

    if (value?.toDate) {
      return value.toDate().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSchemeFiles = (scheme) => {
    if (Array.isArray(scheme?.savedFiles) && scheme.savedFiles.length > 0) {
      return scheme.savedFiles;
    }

    if (Array.isArray(scheme?.files) && scheme.files.length > 0) {
      return scheme.files;
    }

    if (Array.isArray(scheme?.fileNames) && scheme.fileNames.length > 0) {
      return scheme.fileNames.map((fileName) => ({
        fileName,
        fileUrl: "",
      }));
    }

    return [];
  };

  const hasNoResultsYet = extractionId && !hasAnyExtraction;
  const activeSchemeFiles = getSchemeFiles(activeScheme);
  const readableCreatedAt =
    getReadableDate(caseMeta.createdAt) ||
    getReadableDate(caseMeta.createdAtIso) ||
    "";

  const mustPickAdviser =
    isAdviserAdmin && !currentAdviserEmail && !extractionId;

  const breadcrumbItems = [
    { label: "LOA Toolkit", to: "/portal/loa-toolkit" },
    { label: "ProCeding", to: "/pro-ceding/dashboard" },
    {
      label: extractionId
        ? "Ceding Scheme Details"
        : "Ceding Scheme Extraction",
    },
  ];

  return (
    <div className="ceding-page">
      <Breadcrumb items={breadcrumbItems} />

      <section className="loa-hero-card">
        <div>
          <span className="loa-eyebrow-pill">ProCeding</span>
          <h1>Ceding Scheme Extraction</h1>
          <p>
            Upload multiple ceding schemes, extract provider details, review
            source PDFs and track missing information.
          </p>
        </div>
      </section>

      {mustPickAdviser && (
        <Banner
          type="warning"
          message="Please select an adviser in the sidebar before submitting a ceding scheme."
        />
      )}

      {isAdviserAdmin && currentAdviserEmail && !extractionId && (
        <Banner
          type="warning"
          message={`This ceding scheme is being submitted on behalf of ${currentAdviserEmail}. Please ensure you have their consent before proceeding.`}
        />
      )}

      {/* {pageError && <div className="loa-error-banner">{pageError}</div>} */}

      {loadingSaved && (
        <section className="loa-processing-card">
          <div className="loa-spinner" />
          <div>
            <h2>Loading saved extraction...</h2>
            <p>Please wait while the saved ceding scheme is loaded.</p>
          </div>
        </section>
      )}
      <div className={isBusy ? "loa-ui-busy" : ""}>
        {!extractionId && !hasAnyExtraction && (
          <section className="loa-client-card">
            <div className="loa-card-header align-flex-start">
              <div>
                <span className="loa-section-label">Schemes</span>
                <h2>Ceding Schemes</h2>
                <p>
                  Use one scheme per provider / ceding scheme. Each scheme can
                  contain multiple PDFs.
                </p>
                <div className="loa-global-notes">
                  <label>Additional instructions </label>
                  <textarea
                    className="loa-global-notes-input"
                    placeholder="Any additional instructions for the extractions can be added here. "
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    disabled={extractingAll}
                  />
                </div>
              </div>

              <div className="loa-upload-actions">
                {schemes.length >=1 && (
                  <button
                    type="button"
                    className="loa-secondary-btn"
                    onClick={addScheme}
                    disabled={extractingAll || mustPickAdviser}
                  >
                    Add Scheme
                  </button>
                )}

                <button
                  type="button"
                  className="loa-primary-btn"
                  onClick={handleUploadAndExtractAll}
                  disabled={
                    extractingAll ||
                    mustPickAdviser ||
                    !schemes.some(
                      (scheme) =>
                        scheme.files.length > 0 && !scheme.extractionJson,
                    )
                  }
                >
                  {extractingAll ? "Extracting..." : "Upload & Extract"}
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
      {!extractionId &&
        !hasAnyExtraction &&
        schemes.map((scheme, schemeIndex) => (
          <section className="loa-pack-card" key={scheme.id}>
            <div className="loa-card-header">
              <div>
                <span className="loa-section-label">
                  Ceding Scheme {schemeIndex + 1}
                </span>
              </div>

              {schemes.length > 1 && (
                <button
                  type="button"
                  className="loa-secondary-btn"
                  onClick={() => removeScheme(scheme.id)}
                  disabled={extractingAll}
                >
                  Remove Scheme
                </button>
              )}
            </div>

            <section
              className="loa-upload-card loa-drop-zone"
              onDrop={(e) => handleDrop(e, scheme.id)}
              onDragOver={handleDragOver}
            >
              <div className="loa-upload-main">
                <div className="loa-upload-icon">PDF</div>

                <div>
                  <h2>Upload PDF for this scheme</h2>
                  <p>
                    Drag and drop the PDF that belongs to this one ceding
                    scheme/provider.
                  </p>

                  {scheme.files.length > 0 && (
                    <div className="loa-file-list">
                      {scheme.files.map((selectedFile, index) => (
                        <div
                          className="loa-file-pill loa-file-pill-removable"
                          key={`${selectedFile.name}-${index}`}
                        >
                          <span>
                            {index + 1}. {selectedFile.name}
                          </span>

                          <small>
                            {Math.round(selectedFile.size / 1024)} KB
                          </small>
                          <input
                            // type="password"
                            className="loa-file-password-input"
                            placeholder="PDF password if required"
                            value={
                              scheme.filePasswords?.[
                                getFilePasswordKey(selectedFile)
                              ] || ""
                            }
                            onChange={(e) =>
                              updateFilePassword(
                                scheme.id,
                                selectedFile,
                                e.target.value,
                              )
                            }
                            disabled={extractingAll}
                          />

                          <button
                            type="button"
                            className="loa-remove-file-btn"
                            onClick={() =>
                              removeSelectedFileFromScheme(scheme.id, index)
                            }
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* {scheme.error && (
                    <div className="loa-error-banner">{scheme.error}</div>
                  )} */}
                </div>
              </div>

              <div className="loa-upload-actions">
                <label className="loa-file-button">
                  Choose PDF
                  <input
                    type="file"
                    accept="application/pdf"
                    // multiple
                    onChange={(e) => handleFileChange(e, scheme.id)}
                  />
                </label>
              </div>
            </section>

            {scheme.extracting && (
              <section className="loa-processing-card">
                <div className="loa-spinner" />
                <div>
                  <h2>Extracting details...</h2>
                  {/* <p>Treating this scheme’s PDFs as one source pack.</p> */}
                </div>
              </section>
            )}
          </section>
        ))}

      {hasNoResultsYet && (
        <section className="loa-results-card loa-elevated-scheme-card">
          <div className="loa-card-header">
            <div>
              <span className="loa-section-label">Extraction Status</span>
              <h2>
                {caseMeta.status === "failed"
                  ? "Extraction failed"
                  : "Extraction processing"}
              </h2>

              <p>
                {activeScheme?.progressMessage ||
                  caseMeta.progressMessage ||
                  "Files received. Extraction is processing."}
              </p>

              {readableCreatedAt && <p>Submitted: {readableCreatedAt}</p>}
            </div>

            <span
              className={`loa-answer-pill ${
                caseMeta.status === "failed" ? "missing" : "processing"
              }`}
            >
              {caseMeta.status === "failed" ? "Failed" : "Processing"}
            </span>
          </div>

          {caseMeta.error && (
            <div className="loa-error-banner">{caseMeta.error}</div>
          )}

          <section className="loa-details-pdf-split">
            <section>
              <div className="loa-card-header">
                <div>
                  <span className="loa-section-label">Uploaded Files</span>
                  <h2>{activeScheme?.name || "Ceding Scheme"}</h2>
                  <p>
                    Results are not available yet. Uploaded files and
                    instructions are shown below while extraction is processing.
                  </p>
                </div>
              </div>

              {activeSchemeFiles.length > 0 ? (
                <div className="loa-file-list">
                  {activeSchemeFiles.map((fileItem, index) => (
                    <div
                      className="loa-file-pill"
                      key={`${fileItem.fileName || fileItem.name}-${index}`}
                    >
                      <span>
                        {index + 1}.{" "}
                        {fileItem.fileName || fileItem.name || "PDF"}
                      </span>

                      {fileItem.size && (
                        <small>{Math.round(fileItem.size / 1024)} KB</small>
                      )}

                      {fileItem.fileUrl && (
                        <button
                          type="button"
                          className="loa-source-btn"
                          onClick={() =>
                            updateScheme(activeScheme.id, {
                              activeFileIndex: index,
                              pdfPage: 1,
                            })
                          }
                        >
                          Preview
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="loa-pdf-empty">
                  Uploaded file details are not available yet.
                </div>
              )}

              {(caseMeta.additionalInstructions || additionalInstructions) && (
                <section className="loa-notes-readonly-card">
                  <span className="loa-section-label">
                    Additional Instructions
                  </span>
                  <p>
                    {caseMeta.additionalInstructions || additionalInstructions}
                  </p>
                </section>
              )}

              {activeScheme?.notes && (
                <section className="loa-notes-readonly-card">
                  <span className="loa-section-label">Scheme Notes</span>
                  <p>{activeScheme.notes}</p>
                </section>
              )}

              {caseMeta.createdBy && (
                <section className="loa-notes-readonly-card">
                  <span className="loa-section-label">Submitted By</span>
                  <p>{caseMeta.createdBy}</p>
                </section>
              )}
            </section>

            <aside className="loa-pdf-side-panel">
              <div className="loa-pdf-side-header">
                <h2>
                  {activeFile?.fileName || activeFile?.name || "PDF Preview"}
                </h2>

                {activeSchemeFiles.length > 1 && (
                  <div className="loa-file-tabs loa-pdf-file-tabs">
                    {activeSchemeFiles.map((item, index) => (
                      <button
                        key={`${item.fileName || item.name}-${index}`}
                        type="button"
                        className={
                          activeScheme.activeFileIndex === index ? "active" : ""
                        }
                        onClick={() =>
                          updateScheme(activeScheme.id, {
                            activeFileIndex: index,
                            pdfPage: 1,
                          })
                        }
                      >
                        {item.fileName || item.name || `PDF ${index + 1}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="loa-pdf-scroll-wrap">
                {pdfViewerUrl ? (
                  <iframe
                    key={pdfViewerUrl}
                    src={pdfViewerUrl}
                    title="PDF preview"
                    className="loa-pdf-side-frame"
                  />
                ) : (
                  <div className="loa-pdf-empty">
                    PDF preview will appear once the file URL has been saved.
                  </div>
                )}
              </div>
            </aside>
          </section>
        </section>
      )}

      {hasAnyExtraction && (
        <section className="loa-results-card loa-elevated-scheme-card">
          <div className="loa-card-header">
            <div>
              <span className="loa-section-label">Ceding Scheme Summary</span>
            </div>

            <button
              type="button"
              className="loa-secondary-btn"
              onClick={downloadSummaryExcel}
              disabled={!extractedSchemes.length}
            >
              Download Excel
            </button>
          </div>

          {schemes.length > 1 && (
            <div className="loa-table-wrap">
              <table className="loa-table">
                <thead>
                  <tr>
                    <th>Detail</th>
                    {extractedSchemes.map((scheme, index) => (
                      <th key={scheme.id}>
                        {getProviderName(scheme, `Scheme ${index + 1}`)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {summaryQuestions.map((question) => (
                    <tr key={question}>
                      <td className="loa-question-cell">{question}</td>

                      {extractedSchemes.map((scheme) => (
                        <td key={scheme.id}>
                          {getAnswerValue(scheme, question)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeScheme?.savedMessage && (
        <div className="loa-success-banner">{activeScheme.savedMessage}</div>
      )}

      {/* {activeScheme?.error && (
        <div className="loa-error-banner">{activeScheme.error}</div>
      )} */}

      <div className="loa-scheme-dots">
        {extractedSchemes.map((scheme) => {
          const realIndex = schemes.findIndex((item) => item.id === scheme.id);

          return (
            <button
              key={scheme.id}
              type="button"
              className={
                realIndex === activeSchemeIndex
                  ? "loa-scheme-dot-label active"
                  : "loa-scheme-dot-label"
              }
              onClick={() => setActiveSchemeIndex(realIndex)}
              title={getProviderName(scheme)}
            >
              {getProviderName(scheme)}
            </button>
          );
        })}
      </div>
      {/* <Tabs
        tabs={extractedSchemes.map((scheme) => ({
          id: scheme.id,
          label: getProviderName(scheme),
          count: getSchemeRows(scheme).length,
        }))}
        activeTab={activeScheme?.id}
        onTabChange={(schemeId) => {
          const index = schemes.findIndex((scheme) => scheme.id === schemeId);
          if (index !== -1) {
            setActiveSchemeIndex(index);
          }
        }}
      /> */}
      {activeScheme?.extractionJson && (
        <section className="loa-results-card loa-elevated-scheme-card">
          <div className="loa-card-header">
            <div>
              <span className="loa-section-label">Selected Scheme Details</span>
              <h2>{getProviderName(activeScheme)}</h2>
              <p>
                {summary.clientName || "Client not found"}
                {summary.policyNumber
                  ? ` · Policy ${summary.policyNumber}`
                  : ""}
              </p>
            </div>

            {/* <div className="loa-upload-actions">
              <button
                type="button"
                className="loa-secondary-btn"
                onClick={goPreviousScheme}
                disabled={activeSchemeIndex === 0}
              >
                Previous
              </button>

              <button
                type="button"
                className="loa-secondary-btn"
                onClick={goNextScheme}
                disabled={activeSchemeIndex === schemes.length - 1}
              >
                Next
              </button>
            </div> */}
          </div>

          {(caseMeta.additionalInstructions || additionalInstructions) && (
            <section className="loa-notes-readonly-card">
              <span className="loa-section-label">Additional Instructions</span>
              <p>{caseMeta.additionalInstructions || additionalInstructions}</p>
            </section>
          )}

          {activeScheme?.extractionJson?.additionalInstructionsResponse && (
            <section className="loa-notes-readonly-card">
              <span className="loa-section-label">
                Response to Additional Instructions
              </span>
              <p>
                {activeScheme.extractionJson.additionalInstructionsResponse}
              </p>
            </section>
          )}

          <section className="loa-summary-grid">
            <div className="loa-summary-card loa-found">
              <span>{statusDescriptions.found}</span>
              <strong>{counts.found}</strong>
              <p>Found</p>
            </div>

            <div className="loa-summary-card loa-partial">
              <span>{statusDescriptions.partial}</span>
              <strong>{counts.partial}</strong>
              <p>Partial</p>
            </div>

            <div className="loa-summary-card loa-missing">
              <span>{statusDescriptions.missing}</span>
              <strong>{counts.missing}</strong>
              <p>Missing</p>
            </div>

            <div className="loa-summary-card loa-progress">
              <span>Scheme completion</span>
              <strong>{completionPercent}%</strong>
              <div className="loa-progress-bar">
                <div style={{ width: `${completionPercent}%` }} />
              </div>
            </div>
          </section>

          <div className="loa-global-notes">
            <label>Notes</label>
            <textarea
              className="loa-global-notes-input"
              placeholder="Add notes for this ceding scheme..."
              value={activeScheme.notes}
              onChange={(e) =>
                updateScheme(activeScheme.id, { notes: e.target.value })
              }
            />
          </div>

          <div className="loa-client-actions">
            <button
              type="button"
              className="loa-primary-btn"
              onClick={() => handleSaveNotes(activeScheme.id)}
              disabled={activeScheme.saving}
            >
              {activeScheme.saving ? "Saving..." : "Save Notes"}
            </button>
          </div>

          <section className="loa-details-pdf-split">
            <section>
              <div className="loa-card-header">
                <div>
                  <span className="loa-section-label">
                    Ceding Scheme Details
                  </span>
                  <h2>Extracted Answers</h2>
                </div>
              </div>

              <div className="loa-table-wrap">
                <table className="loa-table">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Answer</th>
                      <th>Status</th>
                      <th>Source</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, index) => {
                      const sourcePage = row.source?.page;

                      return (
                        <tr
                          key={`${row.question}-${index}`}
                          className={sourcePage ? "loa-clickable-row" : ""}
                          onClick={() =>
                            goToSourcePage(activeScheme.id, row.source)
                          }
                        >
                          <td className="loa-question-cell">{row.question}</td>
                          <td>{row.answer}</td>
                          <td>
                            <span
                              className={`loa-answer-pill ${
                                row.status || "missing"
                              }`}
                            >
                              {statusLabels[row.status] || row.status}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="loa-source-btn"
                              title={
                                row.source?.snippet || "No source available"
                              }
                              disabled={!row.source?.page}
                              onClick={(e) => {
                                e.stopPropagation();
                                goToSourcePage(activeScheme.id, row.source);
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {showJson && (
                <pre className="loa-json-preview">
                  {JSON.stringify(activeScheme.extractionJson, null, 2)}
                </pre>
              )}
            </section>

            <aside className="loa-pdf-side-panel">
              <div className="loa-pdf-side-header">
                <h2>{activeFile?.fileName || activeFile?.name || "PDF"}</h2>

                {/* {activeScheme.pdfPage && (
                  <p>Viewing page {activeScheme.pdfPage}</p>
                )} */}

                {activeScheme.savedFiles?.length > 1 && (
                  <div className="loa-file-tabs loa-pdf-file-tabs">
                    {activeScheme.savedFiles.map((item, index) => (
                      <button
                        key={`${item.fileName || item.name}-${index}`}
                        type="button"
                        className={
                          activeScheme.activeFileIndex === index ? "active" : ""
                        }
                        onClick={() =>
                          updateScheme(activeScheme.id, {
                            activeFileIndex: index,
                            pdfPage: 1,
                          })
                        }
                      >
                        {item.fileName || item.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="loa-pdf-scroll-wrap">
                {pdfViewerUrl ? (
                  <iframe
                    key={pdfViewerUrl}
                    src={pdfViewerUrl}
                    title="PDF preview"
                    className="loa-pdf-side-frame"
                  />
                ) : (
                  <div className="loa-pdf-empty">PDF file URL not found.</div>
                )}
              </div>
            </aside>
          </section>

          {missingInformation.length > 0 && (
            <section className="loa-missing-card">
              <div className="loa-card-header">
                <div>
                  <span className="loa-section-label">Action Required</span>
                  <h2>Missing / Partial Information</h2>
                </div>

                <span className="loa-count-pill">
                  {missingInformation.length} items
                </span>
              </div>

              <div className="loa-missing-list">
                {missingInformation.map((item, index) => (
                  <div className="loa-missing-item" key={`${item}-${index}`}>
                    <span>{index + 1}</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeScheme.extractionJson.followUpEmail && (
            <section className="loa-email-card">
              <div className="loa-card-header">
                <div>
                  <span className="loa-section-label">Provider Email</span>
                  <h2>Auto-generated Follow-up Email</h2>
                  <p>This only asks for missing or unclear information.</p>
                </div>

                <button
                  type="button"
                  className="loa-secondary-btn"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      activeScheme.extractionJson.followUpEmail,
                    )
                  }
                >
                  Copy Email
                </button>
              </div>

              <pre className="loa-email-preview">
                {activeScheme.extractionJson.followUpEmail}
              </pre>
            </section>
          )}
        </section>
      )}
    </div>
  );
};

export default CedingSchemePage;
